import isObject from 'lodash/isObject';
import isObjectLike from 'lodash/isObjectLike';
import extend from 'lodash/extend';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';
import transform from 'lodash/transform';
import StringUtils from './string';
import _toPath from 'lodash/toPath';
import getIn from 'lodash/get';
import setIn from 'lodash/set';
import isEqual from 'lodash/isEqual';
import { CustomObject } from '../types';

/* eslint-disable */
export const structureObjectKeysAndValues = (props) => {
	function _normalizeValue(value) {
		if (typeof value === 'string' && value.indexOf(',') !== -1 && value.split(',').toString() === value) {
			return value.split(',');
		}

		return value;
	}

	function toLowerCaseFirstLetter(word) {
		const [firstChar, ...otherChars] = word.split('');

		return firstChar.toLowerCase().concat(otherChars.join(''));
	}

	const normalizedCaseProp = Object.keys(props).reduce((memo, key) => {
		const normalizedKey = key
			.split('.')
			.map((key) => {
				return toLowerCaseFirstLetter(key);
			})
			.join('.');

		return {
			...memo,
			[normalizedKey]: props[key],
		};
	}, {});

	const plainProps = {};

	Object.keys(normalizedCaseProp).forEach((key) => {
		const value = normalizedCaseProp[key];

		setIn(plainProps, key, _normalizeValue(value));
	});

	return plainProps;
};

function removeDuplicatedKeysProps(data: CustomObject) {
	const clonedData = cloneDeep(data);
	const keys = Object.keys(clonedData);
	const intermediateData: Record<
		string,
		{
			key: string;
			value: unknown;
		}
	> = {};

	keys.forEach((key) => {
		const loweCaseKey = key.toLowerCase();

		intermediateData[loweCaseKey] = {
			key,
			value: clonedData[key],
		};
	});

	return transform<typeof intermediateData, CustomObject>(
		intermediateData,
		(result, value) => {
			result[value.key] = value.value;
		},
		{},
	);
}

export function stringifyPropsRecursively(input: CustomObject | unknown[]) {
	if (!isObjectLike(input)) {
		return String(input);
	}

	const obj = cloneDeep(input);

	for (let k in obj) {
		if (Array.isArray(obj[k])) {
			obj[k] = obj[k].map((i: CustomObject | unknown[]) => stringifyPropsRecursively(i));
		} else if (isObject(obj[k])) {
			obj[k] = stringifyPropsRecursively(obj[k] as CustomObject);
		} else {
			obj[k] = String(obj[k]);
		}
	}

	return obj;
}

export function transformKeysToLower(obj) {
	return transform(
		obj,
		(result, value, key) => {
			result[String(key).toLowerCase()] =
				isObject(value) && !isArray(value) ? transformKeysToLower(value) : value;
		},
		{},
	);
}

export function hasAnyValue(item) {
	return Object.values(item).some((value) => !StringUtils.isNullOrWhiteSpace(value));
}

export function hasAllValues(item) {
	return Object.values(item).every((value) => !StringUtils.isNullOrWhiteSpace(value));
}

export function hasNoValues(item) {
	return Object.values(item).every((value) => StringUtils.isNullOrWhiteSpace(value));
}

const emptyLiteral = {};

function deleteInWithPath(state, first) {
	if (state === undefined || state === null || first === undefined || first === null) {
		return state;
	}

	for (var _len = arguments.length, rest = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
		rest[_key - 2] = arguments[_key];
	}

	if (rest.length) {
		if (Array.isArray(state)) {
			if (isNaN(first)) {
				throw new Error('Must access array elements with a number, not "' + String(first) + '".');
			}

			var firstIndex = Number(first);

			if (firstIndex < state.length) {
				// @ts-ignore
				var result = deleteInWithPath.apply(void 0, [state && state[firstIndex]].concat(rest));

				if (result !== state[firstIndex]) {
					// @ts-ignore
					var copy = [].concat(state);
					// @ts-ignore
					copy[firstIndex] = result;
					return copy;
				}
			}

			return state;
		}

		if (first in state) {
			var _extends2;
			// @ts-ignore
			var _result = deleteInWithPath.apply(void 0, [state && state[first]].concat(rest));

			return state[first] === _result
				? state
				: extend({}, state, ((_extends2 = {}), (_extends2[first] = _result), _extends2));
		}

		return state;
	}

	if (Array.isArray(state)) {
		if (isNaN(first)) {
			throw new Error('Cannot delete non-numerical index from an array. Given: "' + String(first));
		}

		var _firstIndex = Number(first);

		if (_firstIndex < state.length) {
			// @ts-ignore
			var _copy = [].concat(state);

			_copy.splice(_firstIndex, 1);

			return _copy;
		}

		return state;
	}

	if (first in state) {
		var _copy2 = extend({}, state);

		delete _copy2[first];
		return _copy2;
	}

	return state;
}

export function deleteIn<T>(state: T, field: string): T {
	// @ts-ignore
	return deleteInWithPath.apply(void 0, [state].concat(_toPath(field)));
}

/*
	path should like a dot separated string(if path to array, dot separated numbers is array index)
	example: path = 'address.1.coordinates.y'
	it should delete prop "y" in object address -> second element -> object coordinates
 */
export function deleteInWithCleanUp<T>(state: T, path: string): T {
	if (path[path.length - 1] === ']') {
		// array path
		var pathTokens = _toPath(path);

		pathTokens.pop();
		var parent = getIn(state, pathTokens.join('.'));
		// @ts-ignore
		return parent ? setIn(state, path, undefined) : state;
	}

	var result = state;

	if (typeof getIn(state, path) !== 'undefined') {
		result = deleteIn(state, path);
	}

	var dotIndex = path.lastIndexOf('.');

	if (dotIndex > 0) {
		var parentPath = path.substring(0, dotIndex);

		if (parentPath[parentPath.length - 1] !== ']') {
			var _parent = getIn(result, parentPath);

			if (isEqual(_parent, emptyLiteral)) {
				return deleteInWithCleanUp(result, parentPath);
			}
		}
	}

	return result;
}

/* eslint-enable */

export function excludeObjectKeys<T extends CustomObject, Keys extends keyof T>(
	values: T,
	excludeKeys: Keys[],
): {
	[K in keyof T as K extends Keys ? never : K]: T[K];
} {
	const sanitizedValues = { ...values };

	excludeKeys.forEach((key) => {
		delete sanitizedValues[key];
	});

	return sanitizedValues;
}

export { removeDuplicatedKeysProps };
